import { useRef, useState } from "react"
import { Icon } from "@/components/Icon"
import { useLegalResearchStream } from "./hooks/useLegalResearchStream"
import { useQaHistoryStore } from "./store/qaHistory.store"
import { QaThinking } from "./components/QaThinking"
import { QaAnswerMarkdown } from "./components/QaAnswerMarkdown"
import { QaCitations } from "./components/QaCitations"
import { openPaywallForMe } from "@/features/billing/hooks/useEntitlements"

const SUGGEST = [
  {
    q: "Does the ₹7,500 RWA maintenance exemption apply to the whole amount or only the excess?",
    scope: "GST Law corpus",
  },
  {
    q: "What is the limitation period for issuing an SCN under Section 73 vs Section 74?",
    scope: "GST Law corpus",
  },
  {
    q: "When is interest under Section 50 leviable on the credit-ledger component?",
    scope: "GST Law corpus",
  },
  {
    q: "What is the burden of proof for ITC reversal where the supplier hasn't filed GSTR-1?",
    scope: "GST Law corpus",
  },
]

function generateId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `qa-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/**
 * Q&A workspace, wired to the legal-research SSE endpoint via
 * useLegalResearchStream. History is session-only client state — the
 * backend endpoint has no thread/session concept to persist against.
 */
export function QAView() {
  const [draft, setDraft] = useState("")
  const [askedQuestion, setAskedQuestion] = useState("")
  const [gateError, setGateError] = useState<string | null>(null)
  const asking = useRef(false)
  const { phase, progressLog, answer, error, ask, reset } = useLegalResearchStream()

  const entries = useQaHistoryStore((s) => s.entries)
  const activeId = useQaHistoryStore((s) => s.activeId)
  const addEntry = useQaHistoryStore((s) => s.addEntry)
  const selectEntry = useQaHistoryStore((s) => s.selectEntry)
  const clearActive = useQaHistoryStore((s) => s.clearActive)

  const activeEntry = entries.find((e) => e.id === activeId) ?? null
  const showingLive = !activeEntry
  const question = activeEntry ? activeEntry.question : askedQuestion
  const displayAnswer = activeEntry ? activeEntry.answer : answer

  const runAsk = async (rawQuestion: string) => {
    if (!rawQuestion.trim() || phase === "pending" || asking.current) return
    setGateError(null)
    const action = async () => {
      clearActive()
      setAskedQuestion(rawQuestion)
      setDraft("")
      const result = await ask(rawQuestion)
      if (result) addEntry({ id: generateId(), question: rawQuestion, answer: result, askedAt: Date.now() })
    }
    asking.current = true
    try {
      await action()
    } catch (err) {
      const status = typeof err === "object" && err !== null && "status" in err
        ? (err as { status?: number }).status
        : undefined
      const gated = typeof err === "object" && err !== null && (err as { gated?: boolean }).gated === true
      if (status === 402) {
        if (!gated) openPaywallForMe("nira.message") // body had no quota denial: fall back to /me
      }
      else setGateError(err instanceof Error ? err.message : "Could not reach the research service")
    } finally {
      asking.current = false
    }
  }

  const startNewQuestion = () => {
    reset()
    clearActive()
    setAskedQuestion("")
    setDraft("")
  }

  return (
    <>
      <div className="topbar">
        <span className="topbar__title">Q &amp; A</span>
        <span className="topbar__sub">Ask GST law &amp; your case material</span>
      </div>

      {gateError && <div className="mx-auto mt-3 max-w-4xl rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">{gateError}</div>}

      <div className="qa-wrap">
        <aside className="qa-history" aria-label="Question history">
          <div className="qa-history__head">
            <button className="qa-history__new" onClick={startNewQuestion}>
              <Icon name="plus" size={15} /> New question
            </button>
          </div>
          <div className="qa-history__list">
            {entries.length === 0 ? (
              <div className="empty" style={{ height: "auto", padding: "40px 16px" }}>
                <div className="empty__ico">
                  <Icon name="qa" size={20} />
                </div>
                <h3 style={{ fontSize: 16 }}>No questions yet</h3>
                <p style={{ fontSize: 12 }}>Your asked questions will appear here.</p>
              </div>
            ) : (
              entries.map((entry) => (
                <button
                  key={entry.id}
                  className={`qa-hist${entry.id === activeId ? " is-active" : ""}`}
                  onClick={() => selectEntry(entry.id)}
                >
                  <div className="qa-hist__q">{entry.question}</div>
                </button>
              ))
            )}
          </div>
        </aside>

        <div className="qa">
          <div className="qa__scroll">
            <div className="qa__inner">
              {showingLive && phase === "idle" ? (
                <>
                  <div className="qa__empty">
                    <div className="empty__ico" style={{ margin: "0 auto" }}>
                      <Icon name="qa" size={24} />
                    </div>
                    <h2>Ask GST law &amp; your workspaces</h2>
                    <p>
                      Question the CGST/SGST corpus, notifications, circulars and precedent — or
                      scope a question to a specific workspace in your vault. Every answer is cited to
                      source.
                    </p>
                  </div>
                  <div className="qa__suggest">
                    {SUGGEST.map((s, i) => (
                      <button key={i} className="qa-sg" onClick={() => void runAsk(s.q)}>
                        {s.q}
                        <span className="scope mono">⤷ {s.scope}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="qa-scope-row" style={{ marginBottom: 16 }}>
                    <span className="chip">
                      <Icon name="book" size={13} /> GST Law corpus
                    </span>
                  </div>
                  {question && <h2 className="qa-q">{question}</h2>}

                  {showingLive && phase === "pending" && <QaThinking progressLog={progressLog} />}

                  {showingLive && phase === "error" && (
                    <div className="empty" style={{ height: "auto", padding: "40px 0" }}>
                      <div className="empty__ico">
                        <Icon name="alert" size={22} />
                      </div>
                      <h3>Couldn&apos;t reach the research service</h3>
                      <p>{error ?? "Legal research is temporarily unavailable."}</p>
                      <button
                        className="btn btn--ghost btn--sm"
                        style={{ marginTop: 12 }}
                        onClick={() => void runAsk(question)}
                      >
                        <Icon name="retry" size={14} /> Retry
                      </button>
                    </div>
                  )}

                  {displayAnswer && (!showingLive || phase === "answered") && (
                    <>
                      <QaAnswerMarkdown content={displayAnswer.answer_text} />
                      <QaCitations citations={displayAnswer.citations} evidence={displayAnswer.evidence} />
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="qa__compose">
            <div className="qa__compose-inner">
              <div className="qa-scope-row">
                <span className="lbl">Scope</span>
                <span className="chip">
                  <Icon name="book" size={13} /> GST Law corpus
                </span>
              </div>
              <div className="composer">
                <textarea
                  rows={1}
                  aria-label="Your legal research query"
                  placeholder="Ask a question about GST law or a workspace in your vault…"
                  value={draft}
                  disabled={phase === "pending"}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      void runAsk(draft)
                    }
                  }}
                />
                <button
                  className="composer__send"
                  aria-label="Ask"
                  disabled={!draft.trim() || phase === "pending"}
                  onClick={() => void runAsk(draft)}
                >
                  <Icon name="send" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
